import { Columns3 } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import {
  aplicarMapeamento,
  formatoParaMapear,
  formatoProvavel,
  FORMATOS_PARA_MAPEAR,
  letraDaColuna,
  linhaProvavelDoCabecalho,
  LINHAS_OFERECIDAS_PARA_TITULO,
  problemaDoMapeamento,
  sugerirColunas,
  type ColunasMapeadas,
  type FormatoDaPlanilha,
} from '@obraplan/core/domain/takeoff/mapear-colunas';
import type { AbaDaPlanilha } from '@obraplan/core/domain/takeoff/planilha-de-indices';
import { Button } from '@/components/ui/button';

const SELECT_CLASS =
  'h-11 min-w-0 max-w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

/** As três colunas do campo na tela larga: o rótulo, a escolha e o primeiro valor. */
const GRADE_DO_CAMPO = 'md:grid-cols-[minmax(10rem,1fr)_15rem_minmax(0,1fr)] md:gap-3';

/** O que a tela escolheu: a aba, a linha dos títulos, o formato e a coluna de cada campo. */
interface Escolha {
  readonly aba: string;
  readonly linha: number;
  readonly formato: FormatoDaPlanilha;
  readonly colunas: ColunasMapeadas;
}

function escolhaSugerida(aba: AbaDaPlanilha): Escolha {
  const linha = linhaProvavelDoCabecalho(aba.rows);
  const formato = formatoProvavel(aba.rows, linha);
  return { aba: aba.name, linha, formato, colunas: sugerirColunas(formato, aba.rows, linha) };
}

/** A aba com mais linhas é a que mais provavelmente é a planilha; a capa e o resumo perdem. */
function abaMaior(abas: readonly AbaDaPlanilha[]): AbaDaPlanilha {
  return abas.reduce((maior, aba) => (aba.rows.length > maior.rows.length ? aba : maior));
}

function resumoDaLinha(row: readonly string[]): string {
  const texto = row
    .map((celula) => celula.trim())
    .filter((celula) => celula !== '')
    .join(' · ');
  return texto.length > 70 ? `${texto.slice(0, 69)}…` : texto;
}

/**
 * O mapeamento manual das colunas (22/10/2026): a planilha cujos títulos o reconhecedor não
 * entendeu não para mais num aviso — a pessoa diz qual coluna é cada campo, e a importação
 * segue pelo caminho de sempre (`aplicarMapeamento`, no domínio).
 *
 * Abre com tudo o que dá para adivinhar já escolhido: a aba maior, a linha que mais parece a dos
 * títulos, o formato com mais campos obrigatórios reconhecidos e as colunas que o reconhecedor
 * (ou o conteúdo) achou. Ao lado de cada campo, o primeiro valor da coluna escolhida — é por ele
 * que a pessoa confere sem abrir o Excel.
 */
export function MapearColunas({
  fileName,
  abas,
  onLer,
  onCancelar,
}: {
  readonly fileName: string;
  readonly abas: readonly AbaDaPlanilha[];
  readonly onLer: (formato: FormatoDaPlanilha, abas: readonly AbaDaPlanilha[]) => void;
  readonly onCancelar: () => void;
}) {
  const id = useId();
  const [escolha, setEscolha] = useState<Escolha>(() => escolhaSugerida(abaMaior(abas)));
  const aba = abas.find((candidata) => candidata.name === escolha.aba) ?? abaMaior(abas);
  const { campos, descricao } = formatoParaMapear(escolha.formato);
  const problema = problemaDoMapeamento(escolha.formato, escolha.colunas);

  const titulos = aba.rows[escolha.linha] ?? [];
  const largura = useMemo(
    () =>
      Math.max(
        titulos.length,
        ...aba.rows.slice(escolha.linha + 1, escolha.linha + 51).map((row) => row.length),
      ),
    [aba, escolha.linha, titulos.length],
  );

  const primeiroValor = (coluna: number): string | undefined => {
    if (coluna < 0) return undefined;
    for (let posicao = escolha.linha + 1; posicao < aba.rows.length; posicao += 1) {
      const valor = (aba.rows[posicao]![coluna] ?? '').trim();
      if (valor !== '') return valor;
    }
    return undefined;
  };

  const linhasOferecidas = aba.rows
    .slice(0, LINHAS_OFERECIDAS_PARA_TITULO)
    .map((row, linha) => ({ linha, resumo: resumoDaLinha(row) }));

  return (
    <section
      aria-labelledby={`${id}-titulo`}
      className="flex flex-col gap-3 rounded-md border border-(--color-state-warn) px-3 py-3"
    >
      <div className="flex flex-col gap-1">
        <h3 id={`${id}-titulo`} className="flex items-center gap-2 text-sm font-semibold">
          <Columns3 className="size-4 shrink-0" aria-hidden="true" />
          Diga qual coluna é cada campo
        </h3>
        <p className="max-w-[72ch] text-sm text-(--color-content-muted)">
          Os títulos de <strong className="font-medium text-(--color-content)">{fileName}</strong>{' '}
          não batem pelo nome com nenhum dos três formatos. O que deu para reconhecer já está
          escolhido; confira e complete. A prévia mostra o que vai entrar antes de gravar.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        {abas.length > 1 && (
          <label className="flex min-w-0 max-w-full flex-col gap-1 text-xs text-(--color-content-muted)">
            Aba
            <select
              className={SELECT_CLASS}
              value={aba.name}
              onChange={(event) => {
                const outra = abas.find((candidata) => candidata.name === event.target.value);
                if (outra !== undefined) setEscolha(escolhaSugerida(outra));
              }}
            >
              {abas.map((candidata) => (
                <option key={candidata.name} value={candidata.name}>
                  {candidata.name} ({candidata.rows.length} linhas)
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="flex min-w-0 max-w-full flex-col gap-1 text-xs text-(--color-content-muted)">
          Linha dos títulos
          <select
            className={`${SELECT_CLASS} md:max-w-[26rem]`}
            value={escolha.linha}
            onChange={(event) => {
              const linha = Number(event.target.value);
              setEscolha({
                ...escolha,
                linha,
                colunas: sugerirColunas(escolha.formato, aba.rows, linha),
              });
            }}
          >
            {linhasOferecidas.map(({ linha, resumo }) => (
              <option key={linha} value={linha}>
                Linha {linha + 1} · {resumo}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 max-w-full flex-col gap-1 text-xs text-(--color-content-muted)">
          Formato
          <select
            className={SELECT_CLASS}
            value={escolha.formato}
            onChange={(event) => {
              const formato = event.target.value as FormatoDaPlanilha;
              setEscolha({
                ...escolha,
                formato,
                colunas: sugerirColunas(formato, aba.rows, escolha.linha),
              });
            }}
          >
            {FORMATOS_PARA_MAPEAR.map((candidato) => (
              <option key={candidato.formato} value={candidato.formato}>
                {candidato.rotulo}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="max-w-[72ch] text-xs text-(--color-content-muted)">{descricao}</p>

      {/*
        Linhas e não tabela: no celular cada campo empilha rótulo, coluna e primeiro valor — a
        tabela de três colunas espremia a escolha num "B · [" ilegível. Da largura `md` para
        cima, as três colunas voltam.
      */}
      <div className="flex flex-col divide-y divide-(--color-border-subtle) rounded-md border border-(--color-border-subtle)">
        <div
          aria-hidden="true"
          className={`hidden px-3 py-2 text-xs font-medium text-(--color-content-muted) md:grid ${GRADE_DO_CAMPO}`}
        >
          <span>Campo</span>
          <span>Coluna da planilha</span>
          <span>Primeiro valor</span>
        </div>
        {campos.map((campo) => {
          const coluna = escolha.colunas[campo.campo] ?? -1;
          const idDoCampo = `${id}-${campo.campo}`;
          return (
            <div
              key={campo.campo}
              data-campo={campo.campo}
              className={`grid gap-1.5 px-3 py-2.5 md:items-center ${GRADE_DO_CAMPO}`}
            >
              <div className="text-sm">
                <label htmlFor={idDoCampo} className="font-medium">
                  {campo.rotulo}
                </label>
                {campo.obrigatorio && (
                  <span className="text-xs text-(--color-content-muted)"> · obrigatório</span>
                )}
              </div>
              <select
                id={idDoCampo}
                className={`${SELECT_CLASS} w-full`}
                value={coluna}
                onChange={(event) =>
                  setEscolha({
                    ...escolha,
                    colunas: { ...escolha.colunas, [campo.campo]: Number(event.target.value) },
                  })
                }
              >
                <option value={-1}>{campo.obrigatorio ? 'Escolha a coluna' : 'Não tem'}</option>
                {Array.from({ length: largura }, (_, indice) => (
                  <option key={indice} value={indice}>
                    {letraDaColuna(indice)} · {(titulos[indice] ?? '').trim() || 'sem título'}
                  </option>
                ))}
              </select>
              <span className="min-w-0 truncate text-xs text-(--color-content-muted)">
                <span className="md:hidden">Primeiro valor: </span>
                {primeiroValor(coluna) ?? '—'}
              </span>
            </div>
          );
        })}
      </div>

      {problema !== undefined && <p className="text-sm text-(--color-state-warn)">{problema}</p>}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="primary"
          disabled={problema !== undefined}
          onClick={() =>
            onLer(
              escolha.formato,
              aplicarMapeamento(abas, aba.name, escolha.linha, escolha.formato, escolha.colunas),
            )
          }
        >
          Ler com estas colunas
        </Button>
        <Button type="button" variant="ghost" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </section>
  );
}
